import clsx from 'clsx';
import { memo, MouseEvent, ReactNode, useEffect, useMemo, useRef } from 'react';
import { Link, To } from 'react-router-dom';
import { MarkInProgressIcon } from 'src/modules/common/icons/MarkInProgressIcon.tsx';
import { MarkEmptyIcon } from 'src/modules/common/icons/MarkEmptyIcon.tsx';
import { MarkDoneIcon } from 'src/modules/common/icons/MarkDoneIcon.tsx';
import { I18n } from 'src/modules/common/components/I18n/I18n.tsx';
import { Tooltip } from 'src/modules/common/components/Tooltip/Tooltip.tsx';
import { useIsOverflowing } from 'src/modules/common/hooks/useIsOverflowing.ts';
import { CrossIcon } from 'src/modules/common/icons/CrossIcon.tsx';
import { BREADCRUMB_STATUS } from 'src/common/types/BreadcrumbStatus';
import { LocationState as ValvesLocationState } from 'src/modules/LocationState';
import { LocationState as SensorsLocationState } from 'src/sensors/LocationState';
import { LocationState as ActuatorsLocationState } from 'src/actuators/LocationState';

export type BreadcrumbProps = {
    title: string;
    value: ReactNode;
    tooltipValue?: ReactNode;
    breadcrumbStatus: BREADCRUMB_STATUS;
    to: To;
    linkState?: ValvesLocationState | SensorsLocationState | ActuatorsLocationState;
    disabled?: boolean;
    onClick?: (e: MouseEvent<HTMLAnchorElement>) => void;
    onActionClick?: (e: MouseEvent<HTMLDivElement>) => void;
}

export const Breadcrumb = memo(({
    to,
    breadcrumbStatus,
    title,
    value,
    tooltipValue,
    linkState,
    disabled = false,
    onClick,
    onActionClick,
}: BreadcrumbProps) => {
    const ref = useRef<HTMLAnchorElement | null>(null);
    const titleRef = useRef<HTMLParagraphElement | null>(null);
    const valueRef = useRef<HTMLDivElement | null>(null);

    const isContentTruncated = useIsOverflowing([titleRef, valueRef]);

    const stateIcon = useMemo(() => {
        if (breadcrumbStatus === BREADCRUMB_STATUS.PROGRESS) {
            return <MarkInProgressIcon/>;
        }

        if (breadcrumbStatus === BREADCRUMB_STATUS.EMPTY) {
            return <MarkEmptyIcon/>;
        }

        return <MarkDoneIcon/>;
    }, [breadcrumbStatus]);

    const handleActionClick = (e: MouseEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        onActionClick?.(e);
    };

    const disabledBreadcrumbHandler = (e: MouseEvent<HTMLAnchorElement>) => {
        e.preventDefault();
    };

    useEffect(() => {
        if (breadcrumbStatus === BREADCRUMB_STATUS.PROGRESS && ref.current) {
            ref.current.scrollIntoView?.({ behavior: 'instant', inline: 'center' });
        }
    }, [breadcrumbStatus]);

    return (
        <Tooltip
            title={<I18n messageId={title}/>}
            value={tooltipValue || value}
            disabled={disabled}
            showContent={isContentTruncated}
        >
            <Link
                ref={ref}
                className={clsx({
                    'vss-breadcrumb': true,
                    'vss-breadcrumb--filled': breadcrumbStatus === BREADCRUMB_STATUS.DONE,
                    'vss-breadcrumb--selected': breadcrumbStatus === BREADCRUMB_STATUS.PROGRESS,
                    'vss-breadcrumb--disabled': disabled,
                })}
                onClick={disabled ? disabledBreadcrumbHandler : onClick}
                to={to}
                state={linkState}
            >
                <div className={clsx({
                    'vss-breadcrumb__status-mark': true,
                    'vss-breadcrumb__status-mark--empty': breadcrumbStatus === BREADCRUMB_STATUS.EMPTY,
                })}
                >
                    {stateIcon}
                </div>

                <div className="vss-breadcrumb__contents">
                    <p
                        ref={titleRef}
                        className={clsx({
                            'vss-breadcrumb__title': true,
                            'vss-breadcrumb__title--limited': breadcrumbStatus === BREADCRUMB_STATUS.PROGRESS &&
                            onActionClick,
                        })}
                    ><I18n messageId={title}/>
                    </p>

                    <div ref={valueRef} className="vss-breadcrumb__value">{value}</div>

                    {breadcrumbStatus === BREADCRUMB_STATUS.PROGRESS && onActionClick && (
                        <div
                            data-testid="breadcrumb-action"
                            role="button"
                            tabIndex={0}
                            onKeyDown={() => null}
                            className="vss-breadcrumb__action"
                            onClick={handleActionClick}
                        >
                            <CrossIcon/>
                        </div>
                    )}
                </div>
            </Link>
        </Tooltip>
    );
});
